import { useState, useEffect, useMemo } from 'react';
import { Search, Filter, ShoppingBag } from 'lucide-react';
import { bagsApi, BACKEND_URL } from '../services/api';
import BagCard from '../components/cards/BagCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BagsPage = () => {
  const [bags, setBags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const toImageUrl = (value) => {
    if (!value || typeof value !== 'string') return '/placeholder.png';
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    if (value.startsWith('data:image')) return value;
    return `${BACKEND_URL}${value.startsWith('/') ? value : `/${value}`}`;
  };

  const normalizeBag = (bag, index = 0) => {
    const gallery = Array.isArray(bag?.images)
      ? bag.images
          .map((item) => {
            if (typeof item === 'string') return { img: item };
            if (item && typeof item === 'object' && item.img) return { img: item.img };
            return null;
          })
          .filter(Boolean)
      : [];

    return {
      _id: bag?._id || bag?.id || String(index),
      bagAd: bag?.bagAd || '',
      bagNov: bag?.bagNov || '',
      bagPrice: Number(bag?.bagPrice) || 0,
      bagCategory: bag?.bagCategory || '',
      bagImg: bag?.bagImg || '',
      images: gallery,
      createdAt: bag?.createdAt,
      updatedAt: bag?.updatedAt,
    };
  };

  const extractBags = (responseData) => {
    if (Array.isArray(responseData)) return responseData;
    if (Array.isArray(responseData?.bags)) return responseData.bags;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.items)) return responseData.items;
    return [];
  };

  const fetchBags = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await bagsApi.getAll();
      const rawBags = extractBags(response?.data);
      const normalized = rawBags.map((bag, index) => normalizeBag(bag, index));

      setBags(normalized);
    } catch (err) {
      console.error('Error fetching bags:', err);
      setError('Çantaları yükləmək mümkün olmadı.');
      setBags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBags();
  }, []);

  const categories = useMemo(() => {
    return [...new Set(bags.map((bag) => bag.bagCategory?.trim()).filter(Boolean))];
  }, [bags]);

  const filteredBags = useMemo(() => {
    let result = [...bags];
    const search = searchTerm.trim().toLowerCase();

    if (search) {
      result = result.filter((bag) => {
        const name = bag.bagAd.toLowerCase();
        const type = bag.bagNov.toLowerCase();
        const category = bag.bagCategory.toLowerCase();

        return (
          name.includes(search) ||
          type.includes(search) ||
          category.includes(search)
        );
      });
    }

    if (selectedCategory !== 'all') {
      result = result.filter(
        (bag) => bag.bagCategory.trim().toLowerCase() === selectedCategory.trim().toLowerCase()
      );
    }

    return result;
  }, [bags, searchTerm, selectedCategory]);

  return (
    <div className="min-h-screen bg-brand-cream/30" data-testid="bags-page">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-brand-cream via-brand-accent/20 to-brand-secondary/30 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-gold/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
          <span className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm text-brand-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <ShoppingBag className="w-4 h-4" />
            Kolleksiya
          </span>

          <h1 className="font-serif text-4xl md:text-6xl text-brand-brown">
            Əl işi <span className="text-brand-primary italic">Çantalar</span>
          </h1>

          <p className="mt-4 text-brand-muted max-w-xl mx-auto text-lg">
            Hər biri sevgi və ustalıqla hazırlanmış unikal çantalar
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-brand-secondary/30 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-brand-muted" />
              <Input
                type="text"
                placeholder="Çanta axtar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 rounded-full border-brand-secondary bg-brand-cream/50 focus:border-brand-primary"
                data-testid="bags-search-input"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-2 text-brand-muted">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Kateqoriya:</span>
              </div>

              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger
                  className="w-full sm:w-[220px] rounded-full border-brand-secondary"
                  data-testid="bags-category-filter"
                >
                  <SelectValue placeholder="Hamısı" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Hamısı</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <p className="text-sm text-brand-muted bg-brand-accent/30 px-4 py-2 rounded-full whitespace-nowrap">
                {filteredBags.length} çanta tapıldı
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <LoadingSpinner text="Çantalar yüklənir..." />
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-500">{error}</p>
              <button
                onClick={fetchBags}
                className="mt-4 text-sm text-brand-primary underline"
                data-testid="bags-retry-button"
              >
                Yenidən cəhd et
              </button>
            </div>
          ) : filteredBags.length === 0 ? (
            <EmptyState
              icon={ShoppingBag}
              title="Çanta tapılmadı"
              description={
                searchTerm || selectedCategory !== 'all'
                  ? 'Axtarış meyarlarınızı dəyişin.'
                  : 'Hazırda göstəriləcək çanta yoxdur.'
              }
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBags.map((bag, index) => (
                <BagCard
                  key={bag._id || index}
                  bag={bag}
                  index={index}
                  toImageUrl={toImageUrl}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BagsPage;